import { isInvoicingSessionCancelled } from "components/Dashboard/DoctorDashboard/utils/isInvoicingSessionCancelled";
import { useAppointmentSeriesModalContext } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/appointment-series-modal-context/AppointmentSeriesModalContext";
import { useConfirmAttendanceModalContext } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/confirm-attendance-modal-context/ConfirmAttendanceModalContextProvider";
import getAppointmentUrl from "components/Dashboard/utility/getAppointmentUrl";
import {
  isAIAudioSession,
  isAIAudioSessionForAllPlan,
} from "components/ai-session-review/utils/getAISessionType";
import React, { useCallback, useMemo } from "react";
import { Button, Dropdown } from "react-bootstrap";
import { useDispatch } from "react-redux";
import cancelAppointment from "services/scheduler/cancelAppointment";
import approveNewPatient from "services/client-portal/approveNewPatient";
import { closeSidesheet } from "store/calendarSidebarSlice";
import { PRO_CALENDAR_ANALYTICS_PAGE_NAME } from "utils/amplitude";
import { isInPersonSession } from "utils/online-offline/isInPersonSession";
import DropdownToggle from "components/dropdown-toggle/DropdownToggle";
import TimeslotCancelSessionButton from "../../../../../calendar-view/components/scrollable-calendar-view/components/timeslot-cancel-session-button/TimeslotCancelSessionButton";
import styles from "../../SessionSidesheet.module.scss";
import ReceiptLink from "../receipt-link/ReceiptLink";
import { useTeamContext } from "contexts/group-practice-context/TeamContext";

interface SessionDetailsFooterProps {
  appointment: AppointmentWithSeriesInfo & {
    invoice?: InvoiceData;
  };
  nextStatus: string | null;
  isPartOfASeries: boolean;
  busy: boolean;
  setBusy: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<any>;
  canCancel: boolean;
  isCancelled: boolean;
  shouldShowViewReceiptLink: boolean;
  canSessionBeJoinedAtCurrentTime: boolean;
  trackClick: (name: string) => void;
}

const SessionDetailsFooter = ({
  appointment,
  nextStatus,
  isPartOfASeries,
  busy,
  setBusy,
  setError,
  canCancel,
  isCancelled,
  shouldShowViewReceiptLink,
  canSessionBeJoinedAtCurrentTime,
  trackClick,
}: SessionDetailsFooterProps) => {
  const dispatch = useDispatch();
  const { practiceOwnerOrchidUserId } = useTeamContext();
  const { openConfirmAttendanceModal } = useConfirmAttendanceModalContext();
  const { openAppointmentSeriesModal } = useAppointmentSeriesModalContext();

  const needConfirmAttendance =
    appointment.statusFlags.orchidProMayHaveMissedAppointment;

  const newClientNeedsApproval =
    appointment.statusFlags.pendingClientPortalApproval;
  const someoneBooking =
    appointment.statusFlags.pendingClientPortalApproval ||
    appointment.statusFlags.pendingClientPortalSignupComplete;

  const shouldShowPendingConfirmationFooter = useMemo(
    () => !canSessionBeJoinedAtCurrentTime && needConfirmAttendance,
    [canSessionBeJoinedAtCurrentTime, needConfirmAttendance],
  );

  const showShowStartAudioButton = useMemo(() => {
    return (
      isInPersonSession(appointment) && isAIAudioSessionForAllPlan(appointment)
    );
  }, [appointment]);

  const shouldShowRegularFooter = useMemo(() => {
    if (
      appointment.statusFlags.usingInvoicing &&
      isInPersonSession(appointment) &&
      !isAIAudioSessionForAllPlan(appointment)
    ) {
      return false;
    } else {
      return (canSessionBeJoinedAtCurrentTime || canCancel) && !isCancelled;
    }
  }, [canSessionBeJoinedAtCurrentTime, canCancel, isCancelled, appointment]);

  const shouldShowJoinButton = useMemo(() => {
    if (appointment.statusFlags.usingInvoicing) {
      return (
        !showShowStartAudioButton &&
        !isInvoicingSessionCancelled(appointment, nextStatus)
      );
    }
    return (
      canSessionBeJoinedAtCurrentTime &&
      !appointment.statusFlags.unconfirmedInvite &&
      !showShowStartAudioButton
    );
  }, [
    appointment,
    canSessionBeJoinedAtCurrentTime,
    nextStatus,
    showShowStartAudioButton,
  ]);

  const shouldShowCancelButton = useMemo(
    () => canCancel && !appointment.statusFlags.usingInvoicing,
    [canCancel, appointment],
  );

  const onAcceptNewClient = useCallback(async () => {
    trackClick("accept-client-button");
    if (!appointment.patient?.signup_token) return;

    try {
      await approveNewPatient(appointment.patient.signup_token);
      window.alert("Client approved successfully");
    } catch (error) {
      console.error(error);
    }
  }, [appointment, trackClick]);

  const onRescheduleSession = useCallback(() => {
    trackClick("reschedule-session-button");
  }, [trackClick]);

  const onDeclineNewClient = useCallback(() => {
    trackClick("decline-client-button");
  }, [trackClick]);

  const onPendingConfirmationClick = useCallback(() => {
    trackClick("pending-confirmation-button");
    openConfirmAttendanceModal(
      {
        appointment: appointment,
      },
      PRO_CALENDAR_ANALYTICS_PAGE_NAME,
    );
  }, [appointment, openConfirmAttendanceModal, trackClick]);

  const onCancelClick = useCallback(async () => {
    trackClick("cancel-session-button");

    if (isPartOfASeries) {
      openAppointmentSeriesModal({
        appointment,
        onCancelled: () => dispatch(closeSidesheet()),
      });
      return;
    }

    setBusy(true);

    try {
      await cancelAppointment(
        appointment.appointment_id,
        practiceOwnerOrchidUserId,
      );
      dispatch(closeSidesheet());
    } catch (error) {
      setError(error);
    } finally {
      setBusy(false);
    }
  }, [
    appointment,
    dispatch,
    isPartOfASeries,
    openAppointmentSeriesModal,
    trackClick,
    setBusy,
    setError,
    practiceOwnerOrchidUserId,
  ]);

  if (someoneBooking) {
    return (
      <div className={styles["footer"]}>
        <DropdownToggle
          hideCaret
          variant={"link"}
          title={"More actions"}
          className={styles["more-action"]}
        >
          <Dropdown.Item disabled onClick={onRescheduleSession}>
            Reschedule
          </Dropdown.Item>
          <Dropdown.Item disabled onClick={onDeclineNewClient}>
            Decline
          </Dropdown.Item>
        </DropdownToggle>
        {!newClientNeedsApproval && (
          <Button onClick={onAcceptNewClient}>Accept</Button>
        )}
      </div>
    );
  }

  if (shouldShowPendingConfirmationFooter) {
    return (
      <div className={styles["footer"]}>
        <Button onClick={onPendingConfirmationClick} className={"w-100"}>
          Pending confirmation
        </Button>
      </div>
    );
  }

  const hasRecordingInProgress =
    !appointment.session_recording_upload_metadata?.finished &&
    appointment.session_recording_upload_metadata?.uploadId;
  const isAudioRecordingSession =
    isAIAudioSession(appointment.appointment_type_label) &&
    appointment.session_recording_source === "live-audio-recording";

  if (isAudioRecordingSession && hasRecordingInProgress) {
    return (
      <div className={styles["footer"]}>
        <Button
          className={"popover-footer-primary-btn"}
          onClick={() => {
            trackClick("start-audio-button");
          }}
          as={"a"}
          target="_blank"
          rel="noreferrer noopener"
          href={`/pro-dashboard/audio-recorder?appointment_id=${appointment.appointment_id}&session-source=live-audio-recording`}
        >
          Resume Recording
        </Button>
      </div>
    );
  }

  if (shouldShowRegularFooter) {
    return (
      <div className={styles["footer"]}>
        {shouldShowCancelButton && (
          <TimeslotCancelSessionButton
            className={"popover-footer-secondary-btn"}
            busy={busy}
            disabled={busy}
            onClick={onCancelClick}
          />
        )}

        {shouldShowJoinButton && (
          <Button
            as={"a"}
            className={"popover-footer-primary-btn mr-1"}
            onClick={() => {
              trackClick("join-button");
            }}
            disabled={busy}
            href={getAppointmentUrl(appointment)}
            target="_blank"
            rel="noreferrer noopener"
          >
            Join Now
          </Button>
        )}

        {showShowStartAudioButton && (
          <Button
            className={"popover-footer-primary-btn"}
            onClick={() => {
              trackClick("start-audio-button");
            }}
            as={"a"}
            target="_blank"
            rel="noreferrer noopener"
            href={`/pro-dashboard/audio-recorder?appointment_id=${appointment.appointment_id}&session-source=live-audio-recording`}
          >
            Start Audio Recorder
          </Button>
        )}
      </div>
    );
  }

  if (shouldShowViewReceiptLink) {
    return (
      <div className={styles["footer"]}>
        <ReceiptLink appointment={appointment} trackClick={trackClick} />
      </div>
    );
  }

  return null;
};

export default SessionDetailsFooter;
